import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, Upload, Mail, Phone, ArrowRight } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const OrderConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { orderData } = location.state || {};

  if (!orderData) {
    navigate('/pricing');
    return null;
  }

  const uploadUrl = `${window.location.origin}/upload/${orderData.orderId}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          {/* Success Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/20 border-2 border-green-500 mb-4">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Order Confirmed!
            </h1>
            <p className="text-xl text-gray-300">
              Thank you for choosing Visual Creationz
            </p>
          </div>

          {/* Order Details */}
          <div className="bg-white/5 rounded-2xl p-8 border border-white/10 mb-6">
            <h2 className="text-2xl font-bold mb-4">Order Details</h2>
            <div className="space-y-3 text-gray-300">
              <div className="flex justify-between">
                <span>Order ID:</span>
                <span className="font-mono text-amber-500">{orderData.orderId}</span>
              </div>
              <div className="flex justify-between">
                <span>Package:</span>
                <span className="font-semibold">{orderData.packageName}</span>
              </div>
              <div className="flex justify-between">
                <span>Total:</span>
                <span className="font-semibold">£{orderData.totalPrice}</span>
              </div>
              <div className="flex justify-between">
                <span>Deposit Paid:</span>
                <span className="font-semibold text-green-400">£{orderData.depositAmount}</span>
              </div>
            </div>
          </div>

          {/* QR Code Upload Section */}
          <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border-2 border-blue-500/30 rounded-2xl p-8 mb-6">
            <div className="text-center mb-6">
              <Upload className="w-12 h-12 text-blue-400 mx-auto mb-4" />
              <h2 className="text-3xl font-bold mb-2">Upload Your Media</h2>
              <p className="text-gray-300">
                Scan this QR code with your phone to easily upload photos and videos
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl w-fit mx-auto mb-6">
              <QRCodeSVG 
                value={uploadUrl}
                size={200}
                level="H"
                includeMargin={true}
              />
            </div>

            <div className="text-center mb-4">
              <p className="text-sm text-gray-400 mb-2">Or copy this link:</p>
              <div className="bg-white/5 border border-white/10 rounded-lg p-3 font-mono text-sm break-all">
                {uploadUrl}
              </div>
            </div>

            <button
              onClick={() => navigate(`/upload/${orderData.orderId}`)}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-4 rounded-full font-bold hover:from-blue-600 hover:to-purple-700 transition-all flex items-center justify-center gap-2"
            >
              <Upload size={20} />
              Upload Files Now
              <ArrowRight size={20} />
            </button>
          </div>

          {/* What's Next */}
          <div className="bg-white/5 rounded-2xl p-8 border border-white/10 mb-6">
            <h2 className="text-2xl font-bold mb-4">What Happens Next?</h2>
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500 text-black flex items-center justify-center font-bold">
                  1
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Upload Your Media</h3>
                  <p className="text-sm text-gray-400">
                    Use the QR code above to upload your photos and videos from any device
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500 text-black flex items-center justify-center font-bold">
                  2
                </div>
                <div>
                  <h3 className="font-semibold mb-1">We Create Your Masterpiece</h3>
                  <p className="text-sm text-gray-400">
                    Our team will professionally edit your cinematic presentation with music and effects
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500 text-black flex items-center justify-center font-bold">
                  3
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Review & Receive</h3>
                  <p className="text-sm text-gray-400">
                    You'll receive your video for review, with revisions included in your package
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
            <h2 className="text-2xl font-bold mb-4">Need Help?</h2>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Mail className="text-amber-500" size={20} />
                <span>VisualCreationz@hotmail.com</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="text-amber-500" size={20} />
                <span>07494 164379</span>
              </div>
            </div>
            <p className="text-sm text-gray-400 mt-4">
              A confirmation email has been sent to {orderData.email}
            </p>
          </div>

          {/* Back Button */}
          <div className="text-center mt-8">
            <button
              onClick={() => navigate('/')}
              className="text-amber-500 hover:text-amber-400 font-semibold"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
